import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, CreditCard, Banknote, ArrowLeft, Package } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { getCartItemsWithDetails, getCartTotals, sessionId, clearCart } = useCart();
  const cartItems = getCartItemsWithDetails();
  const { subtotal, shipping, total } = getCartTotals();

  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    notes: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        session_id: sessionId,
        shipping_info: formData,
        payment_method: paymentMethod,
        items: cartItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product.name,
          price: item.product.price,
          quantity: item.quantity,
          image: item.product.image
        })),
        subtotal,
        shipping_cost: shipping,
        total
      };

      const response = await axios.post(`${API}/orders`, orderData);
      
      toast.success('Order placed successfully!');
      navigate(`/order-confirmation/${response.data.id}`);
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex flex-col items-center justify-center px-4">
        <Package className="w-24 h-24 text-[#6B4F2A]/30 mb-6" />
        <h1 className="text-2xl md:text-3xl font-bold text-[#2C1E0F] mb-4">No Items to Checkout</h1>
        <p className="text-[#574021] mb-8 text-center">
          Add some products to your cart first.
        </p>
        <Link to="/shop" className="btn-primary">
          Browse Products
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8E7]" data-testid="checkout-page">
      {/* Header */}
      <div className="bg-white border-b border-[#6B4F2A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center gap-2 text-sm text-[#574021] mb-4">
            <Link to="/" className="hover:text-[#1F7A63] transition-colors">Home</Link>
            <span className="text-[#6B4F2A]/30">/</span>
            <Link to="/cart" className="hover:text-[#1F7A63] transition-colors">Cart</Link>
            <span className="text-[#6B4F2A]/30">/</span>
            <span className="text-[#2C1E0F] font-medium">Checkout</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight" data-testid="checkout-title">
            Checkout
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Shipping & Payment Form */}
            <div className="lg:col-span-2 space-y-8">
              {/* Shipping Information */}
              <div className="bg-white rounded-2xl border border-[#6B4F2A]/10 p-6" data-testid="shipping-form">
                <h2 className="text-xl font-bold text-[#2C1E0F] mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 bg-[#1F7A63] text-white rounded-full flex items-center justify-center text-sm">1</span>
                  Shipping Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#574021] mb-1">Full Name *</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      data-testid="checkout-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      data-testid="checkout-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-1">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="+92 XXX XXXXXXX"
                      required
                      data-testid="checkout-phone-input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#574021] mb-1">Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="House/Building No., Street, Area"
                      required
                      data-testid="checkout-address-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-1">City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      data-testid="checkout-city-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-1">Postal Code</label>
                    <input
                      type="text"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      className="input-field"
                      data-testid="checkout-postal-input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#574021] mb-1">Order Notes (Optional)</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="input-field resize-none"
                      placeholder="Any special instructions for delivery"
                      data-testid="checkout-notes-input"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl border border-[#6B4F2A]/10 p-6" data-testid="payment-form">
                <h2 className="text-xl font-bold text-[#2C1E0F] mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 bg-[#1F7A63] text-white rounded-full flex items-center justify-center text-sm">2</span>
                  Payment Method
                </h2>

                <div className="space-y-4">
                  {/* Cash on Delivery */}
                  <label
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'cod'
                        ? 'border-[#1F7A63] bg-[#1F7A63]/5'
                        : 'border-[#6B4F2A]/20 hover:border-[#1F7A63]/50'
                    }`}
                    data-testid="payment-cod"
                  >
                    <input
                      type="radio"
                      name="payment_method"
                      value="cod"
                      checked={paymentMethod === 'cod'}
                      onChange={() => setPaymentMethod('cod')}
                      className="sr-only"
                    />
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'cod' ? 'border-[#1F7A63]' : 'border-[#6B4F2A]/30'
                    }`}>
                      {paymentMethod === 'cod' && (
                        <div className="w-3 h-3 bg-[#1F7A63] rounded-full" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Banknote className="w-6 h-6 text-[#1F7A63]" />
                        <span className="font-medium text-[#2C1E0F]">Cash on Delivery (COD)</span>
                      </div>
                      <p className="text-sm text-[#574021] mt-1 ml-9">
                        Pay when you receive your order
                      </p>
                    </div>
                  </label>

                  {/* JazzCash */}
                  <label
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'jazzcash'
                        ? 'border-[#1F7A63] bg-[#1F7A63]/5'
                        : 'border-[#6B4F2A]/20 hover:border-[#1F7A63]/50'
                    }`}
                    data-testid="payment-jazzcash"
                  >
                    <input
                      type="radio"
                      name="payment_method"
                      value="jazzcash"
                      checked={paymentMethod === 'jazzcash'}
                      onChange={() => setPaymentMethod('jazzcash')}
                      className="sr-only"
                    />
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'jazzcash' ? 'border-[#1F7A63]' : 'border-[#6B4F2A]/30'
                    }`}>
                      {paymentMethod === 'jazzcash' && (
                        <div className="w-3 h-3 bg-[#1F7A63] rounded-full" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-6 h-6 text-[#E0A106]" />
                        <span className="font-medium text-[#2C1E0F]">JazzCash</span>
                      </div>
                      <p className="text-sm text-[#574021] mt-1 ml-9">
                        Pay via JazzCash mobile wallet
                      </p>
                    </div>
                  </label>

                  {paymentMethod === 'jazzcash' && (
                    <div className="ml-10 p-4 bg-[#E0A106]/10 rounded-xl">
                      <p className="text-sm text-[#574021]">
                        <strong>JazzCash Payment Instructions:</strong><br />
                        After placing your order, you will receive payment details via SMS/WhatsApp.
                        Please transfer the amount to our JazzCash account and share the transaction ID.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-[#6B4F2A]/10 p-6 sticky top-24" data-testid="checkout-summary">
                <h2 className="text-xl font-bold text-[#2C1E0F] mb-6">Order Summary</h2>

                {/* Items */}
                <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                  {cartItems.map((item) => (
                    <div key={item.product_id} className="flex gap-3" data-testid={`checkout-item-${item.product_id}`}>
                      <img
                        src={item.product.image}
                        alt={item.product.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#2C1E0F] text-sm line-clamp-2">{item.product.name}</p>
                        <p className="text-xs text-[#574021]">Qty: {item.quantity}</p>
                        <p className="text-sm font-medium text-[#1F7A63]">
                          Rs. {(item.product.price * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="space-y-3 border-t border-[#6B4F2A]/10 pt-4 mb-6">
                  <div className="flex justify-between text-[#574021]">
                    <span>Subtotal</span>
                    <span data-testid="checkout-subtotal">Rs. {subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-[#574021]">
                    <span>Shipping</span>
                    <span data-testid="checkout-shipping">Rs. {shipping.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-[#6B4F2A]/10 pt-3 flex justify-between text-[#2C1E0F] font-bold text-lg">
                    <span>Total</span>
                    <span data-testid="checkout-total">Rs. {total.toLocaleString()}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-4 flex items-center justify-center gap-2"
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Place Order
                    </>
                  )}
                </button>

                <Link
                  to="/cart"
                  className="flex items-center justify-center gap-2 mt-4 text-[#574021] hover:text-[#1F7A63] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Cart
                </Link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
